import { z } from 'zod';

/**
 * Express-interest DTOs (SIL-1428, slice 4 child 5 of SIL-1319). Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buy-now-and-interest-design.md
 * sections 3.3-3.5.
 *
 * `listingInterestCompanyCategorySchema` mirrors buyer_verification.entity_type
 * (db/schema/950_buyer_verification.sql) exactly — both describe "what kind of
 * org is this" and are kept as the same fixed vocabulary rather than two
 * independently-evolving enums.
 *
 * `buyerTierAtSubmit` is server-set from `clientUser.buyerVerified` — it never
 * appears in the upsert request schema below, only in the response shapes.
 *
 * `buyerRegion` is the OPPOSITE: always client-supplied, never server-derived.
 * It is the buyer's OWN stated region on this interest — not a projection of
 * `buyer_org.pref_regions` (a sourcing preference, per that column's own doc
 * comment, not the buyer's location; conflating the two was a spec error
 * corrected 2026-09-30, see db/schema/973_listing_interest.sql). The API
 * validates it against the live `region` table the same way `pref_regions`
 * is validated (PATCH /api/client/me/org) — this schema only shapes it as a
 * string; the region.id membership check happens at the route.
 */
export const listingInterestCompanyCategorySchema = z.enum([
  'ai_lab',
  'neocloud',
  'enterprise',
  'research',
  'reseller',
]);
export type ListingInterestCompanyCategory = z.infer<typeof listingInterestCompanyCategorySchema>;

export const listingInterestTimelineSchema = z.enum([
  'now',
  '1_3_months',
  '3_6_months',
  '6_plus_months',
]);
export type ListingInterestTimeline = z.infer<typeof listingInterestTimelineSchema>;

export const buyerTierAtSubmitSchema = z.enum(['verified', 'unverified']);
export type BuyerTierAtSubmit = z.infer<typeof buyerTierAtSubmitSchema>;

/**
 * PUT /api/client/listings/:id/interest request body. No `buyerTierAtSubmit`
 * field — the client never sends it (spec 3.3); the route derives it from
 * `clientUser.buyerVerified`.
 */
export const upsertListingInterestInputSchema = z.object({
  companyCategory: listingInterestCompanyCategorySchema.nullable().optional(),
  useCase: z.string().trim().max(500).nullable().optional(),
  timeline: listingInterestTimelineSchema.nullable().optional(),
  message: z.string().trim().max(1000).nullable().optional(),
  /** Buyer-stated region.id — checked against the live `region` table at the API layer. */
  buyerRegion: z.string().trim().min(1).max(64).nullable().optional(),
});
export type UpsertListingInterestInput = z.infer<typeof upsertListingInterestInputSchema>;

/**
 * The caller's OWN interest — GET /api/client/listings/:id/interest, for the
 * edit form. Carries full identity (it's the caller's own row).
 */
export const listingInterestSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  companyCategory: listingInterestCompanyCategorySchema.nullable(),
  useCase: z.string().nullable(),
  timeline: listingInterestTimelineSchema.nullable(),
  message: z.string().nullable(),
  buyerRegion: z.string().nullable(),
  buyerTierAtSubmit: buyerTierAtSubmitSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ListingInterestDTO = z.infer<typeof listingInterestSchema>;

/**
 * The SELLER-facing anonymized row — GET /api/client/listings/:id/interests,
 * owner-only, sourced from v_listing_interest_for_seller (973's
 * SECURITY DEFINER read path). NEVER carries customer_org_id or
 * client_user_id, by construction at the DB layer, not just by omission
 * here — see that migration's header.
 */
export const listingInterestForSellerSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  buyerLabel: z.enum(['Verified buyer', 'Unverified buyer']),
  companyCategory: listingInterestCompanyCategorySchema.nullable(),
  buyerRegion: z.string().nullable(),
  useCase: z.string().nullable(),
  timeline: listingInterestTimelineSchema.nullable(),
  message: z.string().nullable(),
  createdAt: z.string(),
});
export type ListingInterestForSellerDTO = z.infer<typeof listingInterestForSellerSchema>;
